import { useState, useEffect, useCallback, useRef } from 'react';
import { generateMarketEnergyData } from './MarketEnergyAllocation/market-energy-utils';
import { generateOptimalBiddingData, calculateBiddingMetrics } from './OptimalBiddingTable/bidding-table-utils';
import { handleApiError, debounce } from './commercial-optimization-utils';

/**
 * Custom hook for managing commercial optimization data
 * Handles data fetching, caching, and state management
 */
export const useCommercialOptimizationData = (config, selectedRisk = 'All') => {
  const [marketData, setMarketData] = useState(null);
  const [biddingData, setBiddingData] = useState(null);
  const [riskMetrics, setRiskMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  const configRef = useRef(config);
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  // Update config ref when config changes
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Generate mock data (replace with actual API calls)
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      const currentConfig = configRef.current;
      
      if (!currentConfig?.marketEnergyAllocation || !currentConfig?.optimalBiddingTable) {
        throw new Error('Invalid configuration provided');
      }

      // Generate market energy data
      const marketEnergyData = generateMarketEnergyData(
        currentConfig.marketEnergyAllocation?.riskLevels || ['Low Risk', 'Medium Risk', 'High Risk', 'Very High Risk'],
        currentConfig.marketEnergyAllocation?.markets || ['Intra-day', 'Reserve', 'Day-Ahead', 'PPA']
      );

      // Generate optimal bidding data
      const optimalBiddingData = generateOptimalBiddingData(12);

      // Calculate risk metrics
      const metrics = calculateBiddingMetrics(optimalBiddingData, selectedRisk);

      setMarketData(marketEnergyData);
      setBiddingData(optimalBiddingData);
      setRiskMetrics(metrics);
      setLastUpdated(new Date().toISOString());
      retryCountRef.current = 0;

    } catch (err) {
      console.error('Failed to fetch commercial optimization data:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, [selectedRisk]);

  // Retry function
  const retry = useCallback(async () => {
    if (retryCountRef.current < maxRetries) {
      retryCountRef.current += 1;
      await fetchData();
    } else {
      setError(prev => ({
        ...prev,
        message: 'Maximum retry attempts reached. Please refresh the page.',
        canRetry: false
      }));
    }
  }, [fetchData]);

  // Debounced retry function
  const debouncedRetry = useCallback(
    debounce(retry, 1000),
    [retry]
  );

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Recalculate risk metrics when selectedRisk changes
  useEffect(() => {
    if (biddingData) {
      const metrics = calculateBiddingMetrics(biddingData, selectedRisk);
      setRiskMetrics(metrics);
    }
  }, [biddingData, selectedRisk]);

  // Auto-refresh data every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isLoading && !error) {
        fetchData();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [fetchData, isLoading, error]);

  return {
    marketData,
    biddingData,
    riskMetrics,
    isLoading,
    error,
    lastUpdated,
    retry: debouncedRetry,
    refresh: fetchData
  };
};

/**
 * Custom hook for managing commercial optimization UI state
 * Handles selected risk level, filters, and UI interactions
 */
export const useCommercialOptimizationState = (initialRisk = 'All') => {
  const [selectedRisk, setSelectedRisk] = useState(initialRisk);
  const [filters, setFilters] = useState({
    status: 'All',
    strategy: 'All',
    sortBy: 'expectedProfit',
    sortOrder: 'desc'
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [isCompact, setIsCompact] = useState(false);

  // Handle risk level change
  const handleRiskChange = useCallback((risk) => {
    setSelectedRisk(risk);
    setSelectedRow(null); // Clear selection when risk changes
  }, []);

  // Handle filter change
  const handleFilterChange = useCallback((filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  }, []);

  // Handle row selection
  const handleRowSelection = useCallback((row) => {
    setSelectedRow(prev => prev?.id === row.id ? null : row);
  }, []);

  // Handle sort change
  const handleSortChange = useCallback((sortBy) => {
    setFilters(prev => ({
      ...prev,
      sortBy,
      sortOrder: prev.sortBy === sortBy && prev.sortOrder === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  // Toggle compact mode
  const toggleCompact = useCallback(() => {
    setIsCompact(prev => !prev);
  }, []);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setFilters({
      status: 'All',
      strategy: 'All',
      sortBy: 'expectedProfit',
      sortOrder: 'desc'
    });
    setSelectedRow(null);
  }, []);

  return {
    selectedRisk,
    filters,
    selectedRow,
    isCompact,
    handleRiskChange,
    handleFilterChange,
    handleRowSelection,
    handleSortChange,
    toggleCompact,
    resetFilters
  };
};

/**
 * Custom hook for responsive design handling
 * Manages breakpoints and responsive behavior
 */
export const useResponsiveLayout = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const handleResize = debounce(() => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setWindowSize({ width, height });
      setIsMobile(width <= 480);
      setIsTablet(width > 480 && width <= 768);
      setIsDesktop(width > 768);
    }, 150);

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once to set initial state

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    windowSize,
    isMobile,
    isTablet,
    isDesktop,
    breakpoint: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'
  };
};

/**
 * Custom hook for performance optimization
 * Handles data memoization and performance monitoring
 */
export const usePerformanceOptimization = () => {
  const [renderCount, setRenderCount] = useState(0);
  const [averageRenderTime, setAverageRenderTime] = useState(0);
  const renderTimesRef = useRef([]);
  const startTimeRef = useRef(null);

  useEffect(() => {
    startTimeRef.current = performance.now();
    setRenderCount(prev => prev + 1);
    
    return () => {
      if (startTimeRef.current) {
        const renderTime = performance.now() - startTimeRef.current;
        renderTimesRef.current.push(renderTime);
        
        // Keep only last 10 render times
        if (renderTimesRef.current.length > 10) {
          renderTimesRef.current.shift();
        }
        
        // Calculate average
        const avg = renderTimesRef.current.reduce((sum, time) => sum + time, 0) / renderTimesRef.current.length;
        setAverageRenderTime(avg);
        
        // Log performance warnings
        if (renderTime > 100) {
          console.warn(`Slow render detected: ${renderTime.toFixed(2)}ms`);
        }
      }
    };
  });

  const getPerformanceMetrics = useCallback(() => ({
    renderCount,
    averageRenderTime: averageRenderTime.toFixed(2),
    lastRenderTime: renderTimesRef.current[renderTimesRef.current.length - 1]?.toFixed(2) || 0,
    memoryUsage: performance.memory ? {
      used: (performance.memory.usedJSHeapSize / 1024 / 1024).toFixed(2),
      total: (performance.memory.totalJSHeapSize / 1024 / 1024).toFixed(2),
      limit: (performance.memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)
    } : null
  }), [renderCount, averageRenderTime]);

  return {
    renderCount,
    averageRenderTime,
    getPerformanceMetrics
  };
};
